import React from 'react';
import { Empty } from 'antd';
import WOWO from '../../lib/wowo';
import { ModernizationSavingsCard } from '../Card/ModernizationSavingsCard/ModernizationSavingsCard';
import { PotentialSavingsCard } from '../Card/PotentialSavingsCard/PotentialSavingsCard';
import { TopTenOpportunitiesCard } from '../Card/TopTenOpportunitiesCard/TopTenOpportunitiesCard';
import { SummaryDetailsCard } from '../Card';
import { formatStorageSize, calcStorageReductionOpportunities } from './inventorySummaryHelpers';
import { formatCount, formatDecimal } from '../../pages/Discovery/helpers';
import { getGiBfromMiB } from '../../lib/helpers';
import { useGetModernizationSavingsQuery } from '../../api/discovery/inventory';

/**
 * Renders the summary details grid contents.
 * When the new_summary_cards flag is on, renders the new detail cards.
 * Otherwise renders the legacy SummaryDetailsCard set.
 */
export const SummaryDetailsSection = ({ summaryData, inventoryTotals, showCardEllipsis, onIgnoreOptimizations }) => {
    const { data: modernizationPricingData } = useGetModernizationSavingsQuery();
    const { totalInventory, totalReady } = inventoryTotals;
    const storageReductionOpportunities = calcStorageReductionOpportunities(
        totalInventory,
        totalReady,
        summaryData?.undersized_by_storage ?? 0,
    );
    const potentialCPUSavings = summaryData?.total_unused_cpu;

    const potentialRAMSavingsGiB = summaryData?.total_unused_ram
        ? Math.floor(getGiBfromMiB(summaryData.total_unused_ram))
        : 0;
    const potentialRAMSavings = formatStorageSize(potentialRAMSavingsGiB);

    const storageSavingsGiB = summaryData?.total_unused_storage
        ? Math.floor(getGiBfromMiB(summaryData.total_unused_storage))
        : 0;
    const storageSavings = formatStorageSize(storageSavingsGiB);

    const topThreeForRightSizing =
        summaryData?.top_three_for_right_sizing
            ?.filter((machine) => machine != null)
            .map((machine) => {
                const savingsGiB = Math.floor(getGiBfromMiB(machine.total_free_space));
                const formatted = formatStorageSize(savingsGiB);
                return { name: machine.name, savings: formatted.value, unit: formatted.unit };
            }) || [];

    const topThreeForCpuReduction =
        summaryData?.top_three_for_cpu_reduction
            ?.filter((machine) => machine != null)
            .map((machine) => ({
                name: machine.name,
                cpuReduction: machine.cpu_total - machine.cpu_used,
            })) || [];

    if (WOWO.isEnabled('new_summary_cards')) {
        return (
            <>
                <ModernizationSavingsCard
                    title="Modernization Savings"
                    osPricingData={modernizationPricingData}
                    summaryData={summaryData}
                />
                <PotentialSavingsCard
                    title="Potential Savings"
                    details={[
                        { mainLabel: 'CPU', mainValue: `${formatCount(potentialCPUSavings)} vCPUs` },
                        { mainLabel: 'RAM', mainValue: `${formatDecimal(potentialRAMSavings?.value)} ${potentialRAMSavings?.unit}` },
                        { mainLabel: 'Storage', mainValue: `${formatDecimal(storageSavings?.value)} ${storageSavings?.unit}` },
                    ]}
                />
                <TopTenOpportunitiesCard
                    title="Top 10 Opportunities"
                    topTenSavings={summaryData?.top_ten_savings ?? []}
                />
            </>
        );
    }

    return (
        <>
            <SummaryDetailsCard
                title="Overall Optimizations"
                content={[{ text: 'Potential Savings: ', type: 'primary-small' }]}
                details={[
                    { text: 'Ram', value: `${formatDecimal(potentialRAMSavings.value)} ${potentialRAMSavings.unit}` },
                    { text: 'CPU', value: `${formatCount(potentialCPUSavings)} vCPUs` },
                    { text: 'Storage', value: `${formatDecimal(storageSavings.value)} ${storageSavings.unit}` },
                ]}
            />
            <SummaryDetailsCard
                title="Storage Reduction Opportunities"
                onIgnoreOptimizations={() => onIgnoreOptimizations({ storage: [] })}
                showEllipsis={showCardEllipsis}
                content={[
                    { text: formatCount(storageReductionOpportunities), type: 'primary' },
                    { text: `Potential Savings: ${formatDecimal(storageSavings.value)} ${storageSavings.unit}`, type: 'muted' },
                ]}
            />
            <SummaryDetailsCard
                title="Top 3 Machines for Rightsizing"
                details={topThreeForRightSizing.map((machine) => ({
                    text: machine.name,
                    subtext: `Save ${formatDecimal(machine.savings)} ${machine.unit}`,
                    info: true,
                }))}
            >
                {topThreeForRightSizing.length === 0 && (
                    <div className="summary-card-empty-state">
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available" />
                    </div>
                )}
            </SummaryDetailsCard>
            <SummaryDetailsCard
                title="Top 3 Machines for CPU Reduction"
                details={topThreeForCpuReduction.map((machine) => ({
                    text: machine.name,
                    subtext: `Reduce ${formatCount(machine.cpuReduction)} vCPUs`,
                    info: true,
                }))}
            >
                {topThreeForCpuReduction.length === 0 && (
                    <div className="summary-card-empty-state">
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available" />
                    </div>
                )}
            </SummaryDetailsCard>
        </>
    );
};
